import type { Theme } from '@material-ui/core'
import { Radio, RadioGroup, makeStyles } from '@material-ui/core'
import type { RenderableTotals } from '@paintscout/util/builder'
import { getObjectLabels, getTotals } from '@paintscout/util/builder'
import {
  useIsMobile,
  AdornmentBox,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  FormControlLabel,
  Grid,
  InputLabel,
  KeypadInputField,
  Spinner,
  useClientOptions
} from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import { QuoteContext, TotalTable } from '@paintscout/react-quote'
import { jsonToLucene } from 'json-to-lucene'
import clamp from 'lodash/clamp'
import type { QuoteDocument } from 'paintscout'
import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuoteSearch } from 'shared/hooks'
import QuoteTable from 'src/components/common/QuoteTable'

export interface InvoicesDialogProps extends DialogProps {
  quote?: QuoteDocument
  onCancel: () => void
  onCreateInvoice: (args: CreateInvoiceArgs) => void
}

export interface CreateInvoiceArgs {
  partialAmount?: number
  empty?: boolean
}

interface PartialInvoiceAmount {
  amount?: number
  percentage?: number
}

type InvoiceType = 'full' | 'partial' | 'empty'

const useStyles = makeStyles((theme: Theme) => ({
  collapse: {
    padding: theme.spacing(1, 0)
  },
  margin: {
    marginRight: theme.spacing(1)
  },
  quoteTableCard: {
    border: `1px solid ${theme.palette.divider}`
  }
}))

function InvoicesDialog({ onCancel, onCreateInvoice, quote: propQuote, ...dialogProps }: InvoicesDialogProps) {
  let { quote } = useContext(QuoteContext)
  if (propQuote) {
    quote = propQuote
  }
  const { options } = useClientOptions()
  const history = useHistory()
  const classes = useStyles()
  const mobile = useIsMobile({ xs: true })
  const objectLabels = getObjectLabels({ options })
  const hasInvoices = !!(quote.child_invoice || quote.child_invoices?.length > 0 || quote.is_invoice)
  const [loading, setLoading] = useState(false)
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('full')
  const [partialType, setPartialType] = useState<keyof PartialInvoiceAmount>('percentage')
  const [partialAmount, setPartialAmount] = useState<PartialInvoiceAmount>({})
  const [invoices, setInvoices] = useState([])

  const query = jsonToLucene({
    filters: [
      {
        id: 'is_invoice',
        type: 'equal',
        value: true
      },
      {
        type: 'equal',
        not: true,
        id: `archived`,
        value: true
      },
      {
        id: 'trashed',
        type: 'equal',
        not: true,
        value: true
      },
      quote.is_invoice
        ? {
            id: 'parent_quote',
            type: 'equal',
            value: quote.parent_quote
          }
        : {
            type: 'or',
            value: [...(quote.child_invoices ?? []), quote.child_invoice].filter(Boolean).map((id) => ({
              id: 'id',
              type: 'equal',
              value: id
            }))
          }
    ]
  })

  const [search, { loading: loadingResults }] = useQuoteSearch({
    query,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setInvoices(data?.searchQuotes?.quotes ?? [])
    }
  })

  useEffect(() => {
    if (hasInvoices) {
      search({ query, sort: ['-quote_date', '-dates.updated'] })
    }
  }, [])

  const handleCreateInvoice = () => {
    setLoading(true)
    onCreateInvoice({
      partialAmount: determinePartialAmount(),
      empty: invoiceType === 'empty'
    })
  }

  const handleAmountChange = (key: keyof PartialInvoiceAmount, val: string) => {
    const parsed = clamp(parseFloat(val), 0, key === 'amount' ? totals.balanceDue : 100)
    setPartialAmount({
      amount: key === 'amount' ? parsed : (totals.balanceDue * parsed) / 100,
      percentage: key === 'amount' ? (parsed / totals.balanceDue) * 100 : parsed
    })
  }

  const handleDelete = (ids: string[]) => {
    setInvoices(invoices.filter((invoice) => !ids.includes(invoice._id)))
  }

  const quoteTotals = getTotals({ quote, options, consumer: 'customer' })
  const amountPaid = invoices.reduce((sum, invoice) => invoice.totals?.amount_paid + sum, quoteTotals.amountPaid)

  const totals: RenderableTotals = {
    ...quoteTotals,
    showBalanceDue: true,
    additionalWork: quoteTotals.additionalWork,
    balanceDue: quoteTotals.afterTax - amountPaid,
    amountPaid
  }

  const determinePartialAmount = (): number => {
    switch (invoiceType) {
      case 'full':
        // Treat a "Full" invoice as a partial invoice if some amount has already been paid
        return amountPaid > 0 ? totals.balanceDue : undefined
      case 'partial':
        // Treat a "Partial" invoice as a full invoice if the amount is equal to the original balance due
        return partialAmount?.percentage === 100 && !amountPaid
          ? undefined
          : (partialAmount?.percentage / 100) * totals.balanceDue
      default:
        return undefined
    }
  }

  const customAmountOptions = [
    {
      label: '%',
      value: 'percentage'
    },
    {
      label: objectLabels.currency.symbol,
      value: 'amount'
    }
  ]

  return (
    <Dialog maxWidth="lg" fullWidth {...dialogProps}>
      <DialogTitle>Invoices</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <QuoteTable
              classes={{ quoteTableCard: classes.quoteTableCard }}
              loading={loading || loadingResults}
              quotes={invoices as any[]}
              page={1}
              pages={1}
              totalRows={invoices.length}
              rowsPerPage={loading || loadingResults ? 2 : invoices.length}
              disablePagination
              noResultsMessage={'No Invoices'}
              actionsForRow={(row) => ({
                delete: row._id !== quote._id,
                edit: true,
                archive: true
              })}
              bulkActions={{
                delete: true,
                archive: true
              }}
              actionBarAboveBackdrop
              checkboxes={!quote.is_invoice}
              isInvoice
              onDelete={handleDelete}
            />
          </Grid>
          {!quote.is_invoice && (
            <Grid container item justifyContent={mobile ? 'center' : 'space-between'}>
              <Grid item className={classes.margin}>
                <InputLabel shrink>New Invoice</InputLabel>
                <RadioGroup row value={invoiceType} onChange={(e, val) => setInvoiceType(val as InvoiceType)}>
                  <FormControlLabel label={'Full'} value={'full'} control={<Radio color={'primary'} />} />
                  <FormControlLabel label={'Partial'} value={'partial'} control={<Radio color={'primary'} />} />
                  <FormControlLabel label={'Empty'} value={'empty'} control={<Radio color={'primary'} />} />
                </RadioGroup>
                <Collapse show={invoiceType === 'partial'}>
                  <Grid container spacing={1} className={classes.collapse}>
                    <Grid item>
                      <KeypadInputField
                        value={(partialAmount[partialType] ?? '').toString()}
                        fullWidth={true}
                        margin="dense"
                        format={'price'}
                        label={`Amount (${partialType === 'amount' ? objectLabels.currency.symbol : '%'})`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleAmountChange(partialType, e.target.value)
                        }
                        autoSelect={true}
                        endAdornment={
                          <AdornmentBox>
                            <DropdownSelect
                              variant="single"
                              nestedDropdown
                              options={customAmountOptions}
                              value={customAmountOptions.find((option) => option.value === partialType)}
                              onChange={(option) => setPartialType(option.value as keyof PartialInvoiceAmount)}
                            />
                          </AdornmentBox>
                        }
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>
              <Grid item>
                <TotalTable propQuote={propQuote} propTotals={totals} showPaid hideTypeGroups />
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="textNoPadding" onClick={onCancel}>
            Cancel
          </Button>
        }
      >
        {quote.is_invoice ? (
          <>
            <Button
              className={classes.margin}
              variant="text"
              onClick={() => history.push(`/quotes/edit/${quote.parent_quote}`)}
            >
              {`View ${objectLabels.quote.value}`}
            </Button>
            <Button onClick={onCancel}>Done</Button>
          </>
        ) : (
          <Button
            onClick={handleCreateInvoice}
            variant={loading || loadingResults ? 'text' : 'contained'}
            disabled={invoiceType === 'partial' && !partialAmount.amount}
          >
            {loading || loadingResults ? <Spinner size={20} /> : 'Create Invoice'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default InvoicesDialog
